// =====================================================================
// ReceituarioPicker (Receituário / S55) — diálogo pra o médico PUXAR itens do
// receituário dele na hora de emitir uma Receita. Busca + pastas colapsáveis +
// checkboxes → "Adicionar selecionados" devolve os itens pro EmittedDocuments,
// que faz o append numerado no corpo (editável depois).
//
// professionalId = clinic_professionals.id (mesmo id do dropdown da Receita).
// Carrega os itens DELE + os compartilhados da clínica (professional_id NULL).
// =====================================================================
import React, { useState, useEffect, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  Checkbox,
  Typography,
  CircularProgress,
  Collapse,
  Chip,
  Box
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import FolderOutlinedIcon from "@material-ui/icons/FolderOutlined";
import { toast } from "react-toastify";

import {
  listPrescriptionItems,
  prescriptionErrorMessage
} from "../../services/prescriptionService";
import useIsMobile from "../../utils/useIsMobile";

const useStyles = makeStyles((theme) => ({
  search: { marginBottom: theme.spacing(1) },
  loadingWrap: { display: "flex", justifyContent: "center", padding: theme.spacing(4) },
  empty: { color: theme.palette.text.secondary, fontStyle: "italic", padding: theme.spacing(2, 0) },
  folderRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    cursor: "pointer",
    padding: theme.spacing(0.75, 0.5),
    borderRadius: 4,
    userSelect: "none",
    "&:hover": { background: theme.palette.action.hover }
  },
  folderName: { fontWeight: 600, flex: 1, minWidth: 0 },
  itemRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.25, 0, 0.25, 2),
    cursor: "pointer",
    borderRadius: 4,
    "&:hover": { background: theme.palette.action.hover }
  },
  itemText: { flex: 1, minWidth: 0, paddingTop: 9 },
  itemName: { wordBreak: "break-word", lineHeight: 1.25 },
  itemMeta: { color: theme.palette.text.secondary, fontSize: 12, marginTop: 1, wordBreak: "break-word" },
  footerCount: { flex: 1, color: theme.palette.text.secondary, fontSize: 13, paddingLeft: theme.spacing(1) }
}));

// normaliza pra busca sem acento/caixa
const norm = (s) =>
  (s || "")
    .toString()
    .toLowerCase()
    .replace(/[áàâãä]/g, "a")
    .replace(/[éèêë]/g, "e")
    .replace(/[íìîï]/g, "i")
    .replace(/[óòôõö]/g, "o")
    .replace(/[úùûü]/g, "u")
    .replace(/ç/g, "c")
    .replace(/ñ/g, "n");

const ReceituarioPicker = ({ open, professionalId, professionalName, onClose, onConfirm }) => {
  const classes = useStyles();
  const isMobile = useIsMobile();

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState({}); // id -> item
  const [openFolders, setOpenFolders] = useState({}); // folder -> bool

  useEffect(() => {
    if (!open) return undefined;
    setSearch("");
    setSelected({});
    setOpenFolders({});
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const data = await listPrescriptionItems({ professionalId, includeShared: true });
        if (active) setItems(Array.isArray(data && data.items) ? data.items : []);
      } catch (err) {
        if (active) {
          toast.error(prescriptionErrorMessage(err));
          setItems([]);
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [open, professionalId]);

  const isSearching = !!search.trim();

  const { folders, byFolder, total } = useMemo(() => {
    const q = norm(search.trim());
    const filtered = q
      ? items.filter(
          (it) =>
            norm(it.displayName).includes(q) ||
            norm(it.shortName).includes(q) ||
            norm(it.folder).includes(q)
        )
      : items;
    const map = {};
    filtered.forEach((it) => {
      const f = it.folder || "Geral";
      if (!map[f]) map[f] = [];
      map[f].push(it);
    });
    const fl = Object.keys(map).sort((a, b) => {
      if (a === "Geral") return -1;
      if (b === "Geral") return 1;
      return a.localeCompare(b);
    });
    return { folders: fl, byFolder: map, total: filtered.length };
  }, [items, search]);

  const toggleItem = (it) =>
    setSelected((prev) => {
      const next = { ...prev };
      if (next[it.id]) delete next[it.id];
      else next[it.id] = it;
      return next;
    });

  const toggleFolder = (f) => setOpenFolders((prev) => ({ ...prev, [f]: !prev[f] }));

  const selectedList = Object.values(selected);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        Receituário{professionalName ? ` — ${professionalName}` : ""}
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          className={classes.search}
          placeholder="Buscar fórmula, medicamento ou pasta..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          variant="outlined"
          size="small"
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
        />

        {!professionalId && (
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
            Selecione o profissional no formulário pra ver o receituário dele. Mostrando todos os itens da clínica.
          </Typography>
        )}

        {loading ? (
          <div className={classes.loadingWrap}>
            <CircularProgress size={26} />
          </div>
        ) : total === 0 ? (
          <Typography className={classes.empty}>
            {items.length === 0
              ? "Nenhum item no receituário ainda. Cadastre fórmulas em Prontuário → Receituário."
              : "Nenhum resultado pra essa busca."}
          </Typography>
        ) : (
          folders.map((f) => {
            const list = byFolder[f];
            const isOpen = isSearching || !!openFolders[f];
            const selCount = list.filter((it) => selected[it.id]).length;
            return (
              <div key={f}>
                <div className={classes.folderRow} onClick={() => toggleFolder(f)}>
                  {isOpen ? (
                    <ExpandMoreIcon fontSize="small" />
                  ) : (
                    <ChevronRightIcon fontSize="small" />
                  )}
                  <FolderOutlinedIcon fontSize="small" color="action" />
                  <span className={classes.folderName}>{f}</span>
                  {selCount > 0 && (
                    <Chip size="small" color="primary" label={`${selCount} sel.`} />
                  )}
                  <Chip size="small" variant="outlined" label={list.length} />
                </div>
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  {list.map((it) => (
                    <div key={it.id} className={classes.itemRow} onClick={() => toggleItem(it)}>
                      <Checkbox
                        checked={!!selected[it.id]}
                        size="small"
                        color="primary"
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleItem(it)}
                      />
                      <div className={classes.itemText}>
                        <div className={classes.itemName}>
                          {it.displayName}
                          {it.body && it.body.trim() ? (
                            <Chip
                              size="small"
                              label="fórmula"
                              style={{ marginLeft: 6, height: 16, fontSize: 10 }}
                            />
                          ) : null}
                        </div>
                        {it.body && it.body.trim() ? (
                          <div className={classes.itemMeta}>Insere a composição completa</div>
                        ) : it.posology ? (
                          <div className={classes.itemMeta}>{it.posology}</div>
                        ) : it.shortName ? (
                          <div className={classes.itemMeta}>{it.shortName}</div>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </Collapse>
              </div>
            );
          })
        )}
      </DialogContent>
      <DialogActions>
        <Box className={classes.footerCount}>
          {selectedList.length} item(ns) selecionado(s)
        </Box>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          color="primary"
          variant="contained"
          disabled={selectedList.length === 0}
          onClick={() => onConfirm(selectedList)}
        >
          Adicionar selecionados
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReceituarioPicker;
